/**
 * Berechnung des Fachkräftebedarfs / -bestands für die kompakte
 * 4-Zeilen-Tabelle „Stand Fachkräftebedarf in h/Woche" im Belegung-Tab.
 *
 * Quelle der Wahrheit für:
 * - die kleine Übersichtsbox unter Personalbedarf im Belegung-Tab
 * - alle künftigen Stellen, die "wie viele Fachkräfte fehlen" beantworten
 */
import type { PersonalbedarfMonat, PersonalRow } from '@/api/types';
import {
  ausbildungAnteil,
  ausbildungAnteilJahresmittel,
  ausbildungVerlauf,
  hatAusbildungszeitraum,
} from '@/constants/ausbildung';

/**
 * Default, wenn ``plan.mpz_anteil`` nicht gesetzt ist.
 *
 * **0, nicht 15 %** — muss mit dem Backend uebereinstimmen
 * (``belegung.py``: ``mpz = float(plan.mpz_anteil) if ... else 0.0``).
 * Ein leeres Eingabefeld bedeutet „keine zusaetzlichen freiwilligen
 * Fachkraftstunden"; ein Default > 0 wuerde hier Stunden abziehen, die
 * das Backend nie auf ``h_woche`` aufgeschlagen hat, und „Soll" und
 * „Mindestpersonalbedarf nach HKJGB" grundlos auseinanderlaufen lassen.
 */
const DEFAULT_MPZ = 0;

/** Anteil der Monate (1..12), in denen die Stelle aktiv ist. */
export function jahresanteil(p: PersonalRow): number {
  const ab = Math.max(1, p.aktiv_ab_monat ?? 1);
  const bis = Math.min(12, p.aktiv_bis_monat ?? 12);
  return Math.max(0, bis - ab + 1) / 12;
}

/** Ist die Stelle in diesem Monat (1..12) besetzt? */
function istAktivImMonat(p: PersonalRow, monat: number): boolean {
  if (p.tarifart === 'ausbildung' && hatAusbildungszeitraum(p)) {
    return false; // wird im Ausbildungszweig eigens behandelt
  }
  const ab = p.aktiv_ab_monat ?? 1;
  const bis = p.aktiv_bis_monat ?? 12;
  return ab <= monat && monat <= bis;
}

/**
 * Gewicht einer Stelle: Jahresanteil oder "in diesem Monat ja/nein".
 *
 * Der Jahresblick mittelt über zwölf Monate, der Monatsblick zählt die
 * Stelle voll oder gar nicht. Eine Stelle, die nur im zweiten Halbjahr
 * besetzt ist, trägt im Jahresmittel 6/12 bei, im September aber voll —
 * das ist genau der Unterschied, den die Monatsauswahl sichtbar macht.
 */
function gewicht(p: PersonalRow, monat?: number): number {
  if (monat == null) return jahresanteil(p);
  return istAktivImMonat(p, monat) ? 1 : 0;
}

/**
 * Jahresdurchschnittlich vorhandene Fachkraft-Wochenstunden.
 *
 * Reguläre Stellen mit ``fachkraft=true`` zählen mit Faktor 1, Ausbildungs-
 * Stellen mit dem Fachkraft-Anteil aus ``ausbildungAnteil``. Stellen mit
 * Anteil 0 (z.B. nicht-Fachkraft VKA) fallen raus. ``aktiv_ab_monat`` /
 * ``aktiv_bis_monat`` gewichten den Beitrag anteilig — eine Stelle, die
 * nur in Q3+Q4 aktiv ist, zählt mit Faktor 6/12.
 *
 * **FzM-Cap:** Stellen mit ``fzm=true`` werden nach ``sort_order``
 * iteriert; sobald die kumulative FzM-Summe ``fzmLimit`` reißt, zählt
 * die Verletzer-Stelle nur noch mit dem Reststunden-Wert, alle weiteren
 * FzM-Stellen mit 0. ``fzmLimit=Infinity`` (Default) deaktiviert den
 * Cap — nützlich für isolierte Tests.
 */
export function fachkraftestandIst(
  personal: ReadonlyArray<PersonalRow>,
  fzmLimit: number = Infinity,
  foerderjahr?: number,
  monat?: number,
): number {
  // 1) Reguläre Fachkräfte (SuE-Fachkraft ohne FzM) und Ausbildung: voller
  //    Beitrag, kein Cap.
  const sonstiges = personal.reduce((acc, p) => {
    const std = Number(p.std_woche || 0);
    // Ausbildung mit Zeitraum: Der Anteil springt mit dem Ausbildungsjahr
    // mitten im Förderjahr. Das Jahresmittel wiegt die Monate bereits, ein
    // zusätzlicher `jahresanteil` würde doppelt kürzen.
    if (
      p.tarifart === 'ausbildung' &&
      foerderjahr != null &&
      hatAusbildungszeitraum(p)
    ) {
      if (monat == null) {
        return acc + std * ausbildungAnteilJahresmittel(p, foerderjahr);
      }
      // Im Monatsblick zählt das Ausbildungsjahr genau dieses Monats.
      const verlauf = ausbildungVerlauf(p, foerderjahr)[monat - 1];
      return acc + (verlauf ? std * verlauf.anteil : 0);
    }
    let anteil = 0;
    if (p.tarifart === 'ausbildung') {
      anteil = ausbildungAnteil(p.eg_gruppe, p.eg_stufe, p.piva_landesgefoerdert);
    } else if (p.tarifart === 'sue' && p.fachkraft && !p.fzm) {
      anteil = 1;
    }
    if (anteil === 0) return acc;
    return acc + std * anteil * gewicht(p, monat);
  }, 0);

  // 2) FzM-Stellen in sort_order-Reihenfolge bis zum Cap akkumulieren.
  //    Die Stelle, die den Cap reißt, zählt nur mit dem Rest, alle
  //    weiteren mit 0.
  const fzmSorted = personal
    .filter((p) => p.tarifart === 'sue' && p.fzm)
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  let fzmAccum = 0;
  let fzmBeitrag = 0;
  for (const p of fzmSorted) {
    const beitrag = Number(p.std_woche || 0) * gewicht(p, monat);
    if (fzmAccum + beitrag <= fzmLimit) {
      fzmBeitrag += beitrag;
      fzmAccum += beitrag;
    } else if (fzmAccum < fzmLimit) {
      fzmBeitrag += fzmLimit - fzmAccum;
      fzmAccum = fzmLimit;
    }
    // sonst: Cap schon ausgeschöpft, kein Beitrag mehr
  }

  return sonstiges + fzmBeitrag;
}

/**
 * Fachkräftebedarf „soll": Durchschnitt des Mindestpersonalbedarfs
 * inklusive zusätzliche, freiwillige Fachkraftstunden (mpz_anteil).
 *
 * Das ist genau der Wert, den das Backend in ``h_woche`` liefert
 * (netto + 22 % Ausfall + 20 % Leitung + mpz × netto).
 */
export function fachkraftebedarfSoll(
  monatswerte: ReadonlyArray<PersonalbedarfMonat>,
): number {
  if (monatswerte.length === 0) return 0;
  return monatswerte.reduce((s, m) => s + m.h_woche, 0) / monatswerte.length;
}

/**
 * Fachkräftebedarf „min": das gesetzliche Minimum nach HKJGB §32.
 *
 * Abgezogen werden vom ``h_woche`` des Backends:
 *
 * - der **freiwillige mpz-Aufschlag** (``plan.mpz_anteil``) — er ist per
 *   Definition zusätzlich und nicht Teil des Minimums;
 * - die **bewilligten Integrationsstunden**. Sie stammen aus der
 *   Eingliederungshilfe je Kind, nicht aus der Betreuungszeit nach §32.
 *   Sie erhöhen deshalb den *Soll*, aber nicht das gesetzliche Minimum:
 *   Der Mindestpersonalbedarf bleibt derselbe wie ohne Integrationskind
 *   (Vorgabe des Auftraggebers).
 *
 * Rückgerechnet im Frontend aus ``h_woche``, damit kein zweiter
 * Backend-Call nötig ist und der mpz-Aufschlag tagesaktuell aus
 * ``plan.mpz_anteil`` kommt.
 *
 * Ohne mpz-Aufschlag UND ohne Integrationskinder ist das Ergebnis
 * identisch mit ``fachkraftebedarfSoll`` — die beiden Zeilen der Tabelle
 * dürfen nur auseinanderfallen, wenn wirklich eines von beidem vorliegt.
 *
 * Dieselbe Größe trägt auch den FzM-Cap (30 % nach HKJGB). Bis hierher
 * gab es dafür eine zweite, gleichlautende Funktion
 * (``fachkraftebedarfMinOhneIntegration``); seit Mindestbedarf und
 * Cap-Basis dieselbe Definition haben, wäre das nur noch ein zweiter
 * Name für dieselbe Sache — und damit eine Stelle, an der die beiden
 * auseinanderlaufen könnten.
 */
export function fachkraftebedarfMin(
  monatswerte: ReadonlyArray<PersonalbedarfMonat>,
  mpzFactor: number | null,
): number {
  if (monatswerte.length === 0) return 0;
  const f = mpzFactor ?? DEFAULT_MPZ;
  return (
    monatswerte.reduce(
      (s, m) => s + (m.h_woche - m.netto_h_woche * f - (m.integration_h_woche ?? 0)),
      0,
    ) / monatswerte.length
  );
}
